/**
 * @file Factory for document-import S3 multipart API handlers.
 * Maps generic register/finalize calls to `MultiPartS3Upload` API shape.
 */

import type { MultiPartS3Upload } from '@/api/client';

import type {
  S3MultipartFinalizePart,
  S3MultipartHandlers,
} from './create-s3-multipart-plugin';

/** Minimal API surface for S3 multipart register and finalize endpoints. */
export type DocumentImportMultipartApi = {
  register: (body: MultiPartS3Upload) => Promise<MultiPartS3Upload>;
  finalize: (body: MultiPartS3Upload) => Promise<unknown>;
};

export type MultipartRegisterBodyInput = {
  fileName: string;
  contentType: string;
  numberOfParts: number;
};

export type MultipartFinalizeBodyInput = {
  fileName: string;
  uploadId: string;
  parts: S3MultipartFinalizePart[];
  partUrlStore: Record<number, string>;
};

export type DocumentImportMultipartBodyBuilders = {
  register: (input: MultipartRegisterBodyInput) => MultiPartS3Upload;
  finalize: (input: MultipartFinalizeBodyInput) => MultiPartS3Upload;
};

/**
 * Creates {@link S3MultipartHandlers} from document-import API functions.
 * @param api - Orval-generated register/finalize functions for the feature endpoint.
 * @param buildBodies - Feature-specific mappers from handler inputs to API body shape.
 */
export const createDocumentImportMultipartHandlers = (
  api: DocumentImportMultipartApi,
  buildBodies: DocumentImportMultipartBodyBuilders
): S3MultipartHandlers => ({
  register: async ({ fileName, contentType, numberOfParts }) =>
    api.register(
      buildBodies.register({ fileName, contentType, numberOfParts })
    ),
  finalize: async ({ fileName, uploadId, parts, partUrlStore }) =>
    api.finalize(
      buildBodies.finalize({ fileName, uploadId, parts, partUrlStore })
    ),
});
